import { Popover } from "@headlessui/react";
import { ChevronDownIcon, ChevronRightIcon, PlusIcon } from "@heroicons/react/24/outline";
import React, { useState } from "react";

import { ReportGoalType, ReportTargetType } from "@api/graphql/types-and-hooks";
import { DomainDescription } from "@components/DomainDescription/DomainDescription";
import { DomainActionsMenu } from "@pages/ClinicalReportDetails/ReportPage/Components/DomainActionsMenu";
import { GoalRow } from "@pages/ClinicalReportDetails/ReportPage/Components/GoalRow";
import { GroupedDomainRef } from "@pages/ClinicalReportDetails/ReportPage/utils/sortGoalsByDomain";

interface DomainCardProps {
  domain?: GroupedDomainRef | null;
  /** goal id → 0-based report goal number. Report-excluded goals are absent (rendered unnumbered). */
  goalNumberById: Map<string, number>;
  domainName: string;
  goals: ReportGoalType[];
  selectedGoalId: string | undefined;
  selectedTargetId: string | undefined;
  /** When set from sidebar "Expand/Collapse all", controls whether each goal's targets are expanded. */
  expandAll?: boolean;
  /** When true, only unapproved targets are shown; approved goals with unapproved targets appear as ghost anchors. */
  showUnapprovedOnly?: boolean;
  onGoalClick: (goal: ReportGoalType) => void;
  onTargetClick: (target: ReportTargetType, goal: ReportGoalType) => void;
  isRefreshingProgramming?: boolean;
}

export const DomainCard: React.FC<DomainCardProps> = ({
  goalNumberById,
  domainName,
  domain,
  goals,
  selectedGoalId,
  selectedTargetId,
  expandAll,
  showUnapprovedOnly = false,
  onGoalClick,
  onTargetClick,
  isRefreshingProgramming = false,
}) => {
  const [expanded, setExpanded] = useState<boolean | undefined>(expandAll ?? true);

  return (
    <div>
      <div className="flex h-10 items-center gap-2 border-b border-border-medium bg-[rgba(61,58,52,0.04)] pl-1 pr-3">
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <div className="flex shrink-0 items-center">
            <button className="shrink-0 rounded-sm p-2" onClick={() => setExpanded((prev) => !prev)}>
              {expanded ? (
                <ChevronDownIcon className="h-3 w-3 text-icon-secondary" />
              ) : (
                <ChevronRightIcon className="h-3 w-3 text-icon-secondary" />
              )}
            </button>
          </div>
          <button
            className="min-w-0 flex-1 truncate text-left text-sm font-semibold leading-6 text-text-primary"
            onClick={() => setExpanded((prev) => !prev)}
            title={domainName}
          >
            {domainName}
          </button>
        </div>
        <Popover className="relative">
          {({ close }) => (
            <>
              <Popover.Button className="flex items-center justify-center w-8 h-6 rounded-sm hover:bg-limestone-200 transition-colors focus:outline-hidden">
                <PlusIcon className="w-4 h-4 text-icon-secondary" strokeWidth={2} />
              </Popover.Button>
              <Popover.Panel className="absolute right-0 z-10 mt-1 w-56 rounded-lg bg-white shadow-lg ring-1 ring-black/5 focus:outline-hidden">
                <DomainActionsMenu close={close} domain={domain} />
              </Popover.Panel>
            </>
          )}
        </Popover>
      </div>

      {!!domain?.description && (
        <div data-testid="domain-description" className="border-b border-border-medium px-3 py-2">
          <DomainDescription content={domain.description} />
        </div>
      )}

      {expanded && !!goals.length && (
        <div className="flex flex-col gap-2 border-b border-border-strong py-2 px-1">
          {goals.map((goal) => (
            <GoalRow
              key={goal.id}
              goal={goal}
              goalIndex={goalNumberById.get(goal.id)}
              isSelected={selectedGoalId === goal.id && !selectedTargetId}
              selectedTargetId={selectedTargetId}
              onGoalClick={onGoalClick}
              onTargetClick={onTargetClick}
              defaultExpanded={expandAll}
              showUnapprovedOnly={showUnapprovedOnly}
              isRefreshingProgramming={isRefreshingProgramming}
            />
          ))}
        </div>
      )}
    </div>
  );
};
